import type { KeyboardEvent } from 'react';
import { useHistory } from 'react-router-dom';
import { Card } from '@components/atoms/Card';
import Alert from '@components/atoms/Alert';
import TextSnippetOutlinedIcon from '@mui/icons-material/TextSnippetOutlined';
import { formatActivityTime, formatForDisplay } from '@utils/formatDate';
import useFormattedCurrency from '@hooks/useFormattedCurrency';
import CreateNewFolderOutlinedIcon from '@mui/icons-material/CreateNewFolderOutlined';
import { Badge } from '@components/atoms/Badge';
import { cn } from '@utils/cn';
import { ActivityHeader } from '../ActivityHeader';
import { TaskName } from '../TaskName';
import { useTranslation } from '@hooks/useTranslation';

const formatDate = (date?: string | Date | null, notAvailable = 'N/A') =>
  date ? formatForDisplay(date) : notAvailable;

export const ActivityBudgetRequestCreated = ({
  activity,
  handleOpenTaskDetail,
}: {
  activity: any;
  handleOpenTaskDetail?: (taskId: string) => void;
}) => {
  const tActivityCards = useTranslation('activityCards');
  const history = useHistory();
  const budgetRequestId = activity.budgetRequest?.budgetRequestId;

  // Sprint 16, story-076 (GRpkXE4U). This card had no navigation at all — clicking anywhere on
  // it now opens the bid on `/budgets`, "even one not on this page" per that page's existing
  // deep-link handling. Guarded on `budgetRequestId` being present (optional on `Activity`).
  // `TaskName`'s own click already calls `stopPropagation`, so it keeps opening the task-detail
  // modal exactly as today, taking precedence over this card-level handler.
  const handleCardClick = () => {
    if (budgetRequestId) {
      history.push(`/budgets?budgetRequestId=${budgetRequestId}`);
    }
  };

  const handleCardKeyDown = (event: KeyboardEvent) => {
    // Sprint 16, develop run 2 (fix for regression found during review, applied here for
    // consistency with ActivityIncident even though this card has no nested interactive
    // controls today). Only act when the key event originated on the card element itself, not on
    // a bubbled keydown from a descendant.
    if (event.target !== event.currentTarget) return;
    if (!budgetRequestId) return;
    if (event.key === 'Enter' || event.key === ' ') {
      event.preventDefault();
      handleCardClick();
    }
  };

  const maxBudget = activity.budgetRequest?.maxBudgetAmount || null;
  const formattedMaxBudget = useFormattedCurrency(maxBudget);

  const requestedStart = activity.budgetRequest?.requestedStartDate;
  const requestedEnd = activity.budgetRequest?.requestedEndDate;

  const recipients = activity.budgetRequest?.recipients || [];

  const rightLabel = (
    <div className="flex gap-1">
      <TextSnippetOutlinedIcon fontSize="small" className="text-indigo-500  " />
      <p className="text-sm text-indigo-500">{tActivityCards.budgetRequestCreated.bidRequestCreated}</p>
      <div className="flex gap-1">
        <p className="text-sm text-neutral-500">{`- ${formatActivityTime(activity.createdAt)}`}</p>
      </div>
    </div>
  );

  const originatorOrg = activity.budgetRequest?.originatorOrganization as
    | { organizationId?: string; name?: string }
    | undefined;

  return (
    <Card
      key={activity.activityId}
      className={cn('w-full', budgetRequestId && 'cursor-pointer hover:ring-1 hover:ring-indigo-100')}
      onClick={budgetRequestId ? handleCardClick : undefined}
      onKeyDown={budgetRequestId ? handleCardKeyDown : undefined}
      role={budgetRequestId ? 'button' : undefined}
      tabIndex={budgetRequestId ? 0 : undefined}>
      <ActivityHeader activity={activity} rightLabel={rightLabel} />

      <div className="flex gap-1 mt-2">
        <TaskName
          task={activity.task}
          taskId={activity.taskId || activity.task?.taskId}
          onOpenTask={handleOpenTaskDetail}
        />
      </div>

      <div className="flex gap-2 w-full">
        <div className="flex flex-col flex-1 gap-2">
          <Alert className="mt-2">
            <div className="flex flex-col gap-1">
              <div className="flex gap-1">
                <p className="text-sm text-neutral-800 font-semibold">
                  {tActivityCards.taskTypeLabel}
                </p>
                {activity.task?.taskType && (
                  <p className="text-sm text-neutral-800">
                    {activity.task.taskType.split('_').join(' ')}
                  </p>
                )}
              </div>
              <div className="flex gap-1">
                <p className="text-sm text-neutral-800 font-semibold">
                  {tActivityCards.projectLabel}
                </p>
                <p className="text-sm text-neutral-800">
                  {activity.project?.name || tActivityCards.notAvailable}
                </p>
              </div>
            </div>
          </Alert>
          <Alert variant="warning" className="h-full" border>
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-1">
                <CreateNewFolderOutlinedIcon
                  fontSize="small"
                  className="text-yellow-950 -mt-1"
                />
                <p className="text-sm text-yellow-950 font-semibold">
                  {tActivityCards.createdByLabel.replace('{org}', originatorOrg?.name || tActivityCards.notAvailable)}
                </p>
              </div>
            </div>
            <div className="mt-2 space-y-1">
              <p className="text-sm text-neutral-800">
                <span className="font-semibold">{tActivityCards.maxBidLabel}</span>{' '}
                {formattedMaxBudget}
              </p>
              <p className="text-sm text-neutral-800">
                <span className="font-semibold">{tActivityCards.timelineLabel}</span>{' '}
                {formatDate(requestedStart, tActivityCards.notAvailable)} -{' '}
                {formatDate(requestedEnd, tActivityCards.notAvailable)}
              </p>
              {recipients.length > 0 && (
                <div className="flex items-center gap-2 flex-wrap">
                  <p className="text-sm text-yellow-950 font-semibold">
                    {tActivityCards.sentToLabel}
                  </p>
                  {recipients.map((r: any) => (
                    <Badge
                      key={r.recipientOrganizationId}
                      size="sm"
                      variant="primary">
                      {r.recipientOrganization?.name || tActivityCards.notAvailable}
                    </Badge>
                  ))}
                </div>
              )}
            </div>
          </Alert>
          {activity.budgetRequest?.description && (
            <Alert className="h-full">
              <div className="flex items-start justify-between">
                {activity.budgetRequest?.description}
              </div>
            </Alert>
          )}
        </div>
      </div>
    </Card>
  );
};
